// ---------------------------------------------------------------------------
// imageAdjustments.js — Spec B: image filters / transform / border panel.
//
// Registers ONE contextual panel section (visible only when a single fabric
// image is selected) into the foundation's addPanelSection seam. Everything is
// driven through the editor API from DesignEditor.js — this module owns NO
// canvas event and NO canvas mode; it only mutates the active image object and
// routes history through editor.pushHistory / pushHistoryDebounced.
//
// What it exposes to the user (all fabric-native, so it round-trips through the
// foundation's save/load + undo without any custom serialization props):
//   - Sliders over fabric built-in filters: Brightness, Contrast, Saturation,
//     Vibrance, HueRotation, Blur, Noise, Pixelate.
//   - One-click Grayscale / Sepia / Invert / BlackWhite + Sharpen (Convolute).
//   - Flip H / V (flipX/flipY), replace-image (via editor.replaceImage seam,
//     preserving transform), image border (stroke/strokeWidth, strokeUniform),
//     corner radius (a rounded-rect clipPath bound to the image), reset-all.
//
// The existing Shadow / Glow controls are NOT duplicated here — they live in the
// foundation's native left-panel "Umbra / Stralucire" section and stay reachable
// for images (and every object type) exactly as before.
//
// History discipline (spec): applyFilters() is coalesced to one call per animation
// frame during a drag (live preview without thrash), and exactly ONE history
// snapshot is pushed per gesture via editor.pushHistoryDebounced. Discrete actions
// (toggles, flips, reset) push immediately. The filter DATA on obj.filters is set
// synchronously before any snapshot, so undo/save capture the correct state even
// though the GPU texture recompute is deferred to the next frame.
// ---------------------------------------------------------------------------

import React, { useEffect, useRef, useState } from 'react';
import './imageAdjustments.css';

const SHARPEN_MATRIX = [0, -1, 0, -1, 5, -1, 0, -1, 0];

const DEFAULT_ADJ = {
  brightness: 0, // -100..100  -> fabric -1..1
  contrast: 0, // -100..100
  saturation: 0, // -100..100
  vibrance: 0, // -100..100
  hue: 0, // -180..180 deg -> fabric rotation -1..1 (* PI)
  blur: 0, // 0..50 -> fabric blur 0..0.5
  noise: 0, // 0..200
  pixelate: 1, // 1..30 blocksize (1 = off)
  grayscale: false,
  sepia: false,
  invert: false,
  blackwhite: false,
  sharpen: false,
};

// Bump the WebGL texture ceiling so up-to-2560px product photos are filtered on
// the GPU in one pass instead of falling back to the slow 2D backend. Guarded:
// only bump, never lower, only if this fabric build exposes the knob.
function bumpTextureSize(fabric) {
  try {
    if (fabric && typeof fabric.textureSize === 'number' && fabric.textureSize < 4096) {
      fabric.textureSize = 4096;
    }
  } catch (e) { /* leave the default; filtering still works via 2D fallback */ }
}

// Map the active image's fabric filters array back onto the slider/toggle state
// (so the panel reflects reality after selecting an image, undo/redo, or reload).
function readAdjustments(obj) {
  const a = { ...DEFAULT_ADJ };
  const filters = (obj && obj.filters) || [];
  filters.forEach((f) => {
    if (!f) return;
    switch (f.type) {
      case 'Brightness': a.brightness = Math.round((f.brightness || 0) * 100); break;
      case 'Contrast': a.contrast = Math.round((f.contrast || 0) * 100); break;
      case 'Saturation': a.saturation = Math.round((f.saturation || 0) * 100); break;
      case 'Vibrance': a.vibrance = Math.round((f.vibrance || 0) * 100); break;
      case 'HueRotation': a.hue = Math.round((f.rotation || 0) * 180); break;
      case 'Blur': a.blur = Math.round((f.blur || 0) * 100); break;
      case 'Noise': a.noise = Math.round(f.noise || 0); break;
      case 'Pixelate': a.pixelate = f.blocksize || 1; break;
      case 'Grayscale': a.grayscale = true; break;
      case 'Sepia': a.sepia = true; break;
      case 'Invert': a.invert = true; break;
      case 'BlackWhite': a.blackwhite = true; break;
      case 'Convolute': a.sharpen = true; break;
      default: break;
    }
  });
  return a;
}

// Rebuild the fabric filters array from the slider/toggle state. Only non-neutral
// entries are pushed so the filter pipeline stays as short as possible. Order:
// color-space presets, then continuous adjustments, then sharpen last.
function buildFilters(fabric, a) {
  const F = fabric.Image.filters;
  const out = [];
  if (a.grayscale) out.push(new F.Grayscale());
  if (a.blackwhite) out.push(new F.BlackWhite());
  if (a.sepia) out.push(new F.Sepia());
  if (a.invert) out.push(new F.Invert());
  if (a.brightness) out.push(new F.Brightness({ brightness: a.brightness / 100 }));
  if (a.contrast) out.push(new F.Contrast({ contrast: a.contrast / 100 }));
  if (a.saturation) out.push(new F.Saturation({ saturation: a.saturation / 100 }));
  if (a.vibrance) out.push(new F.Vibrance({ vibrance: a.vibrance / 100 }));
  if (a.hue) out.push(new F.HueRotation({ rotation: a.hue / 180 }));
  if (a.blur) out.push(new F.Blur({ blur: a.blur / 100 }));
  if (a.noise) out.push(new F.Noise({ noise: a.noise }));
  if (a.pixelate > 1) out.push(new F.Pixelate({ blocksize: a.pixelate }));
  if (a.sharpen) out.push(new F.Convolute({ matrix: SHARPEN_MATRIX }));
  return out;
}

// Corner radius as a rounded-rect clipPath bound to the image's own coordinate
// space (scales with the image). pct 0 removes it; 100 = fully rounded on the
// short side. obj.width/height are the source pixel dims (constant under scale).
function readRadiusPct(obj) {
  const cp = obj && obj.clipPath;
  if (!cp || cp.type !== 'rect' || !cp.rx) return 0;
  const half = Math.min(obj.width, obj.height) / 2;
  if (!half) return 0;
  return Math.max(0, Math.min(100, Math.round((cp.rx / half) * 100)));
}

function applyRadius(fabric, obj, pct) {
  if (!obj) return;
  if (pct <= 0) {
    obj.clipPath = null;
  } else {
    const r = (pct / 100) * (Math.min(obj.width, obj.height) / 2);
    obj.clipPath = new fabric.Rect({
      width: obj.width, height: obj.height, rx: r, ry: r,
      originX: 'center', originY: 'center', left: 0, top: 0,
    });
  }
  obj.dirty = true;
  obj.setCoords();
}

function Slider({ label, min, max, step, value, suffix, onChange }) {
  return (
    <div className="de-row">
      <label className="de-label">{label}</label>
      <input
        type="range" min={min} max={max} step={step || 1} value={value}
        onChange={onChange} className="de-range"
      />
      <span className="de-val">{value}{suffix || ''}</span>
    </div>
  );
}

function ImageAdjustmentsPanel({ editor, obj }) {
  const fabric = editor.fabric;
  const [adj, setAdj] = useState(DEFAULT_ADJ);
  const [border, setBorder] = useState({ color: '#000000', width: 0 });
  const [radius, setRadius] = useState(0);

  // Authoritative mirrors so a change handler never reads a stale render closure.
  const adjRef = useRef(adj);
  const borderRef = useRef(border);
  const rafRef = useRef(null);
  const replaceInputRef = useRef(null);

  // Re-sync the whole panel whenever the selected image object identity changes
  // (selection change, replace, undo/redo replay). Not on every re-render, so a
  // slider drag is never clobbered mid-gesture.
  useEffect(() => {
    if (!obj) return;
    const a = readAdjustments(obj);
    adjRef.current = a;
    setAdj(a);
    const b = {
      color: (typeof obj.stroke === 'string' && obj.stroke) || '#000000',
      width: obj.strokeWidth || 0,
    };
    borderRef.current = b;
    setBorder(b);
    setRadius(readRadiusPct(obj));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [obj]);

  // Cancel any pending frame on unmount (panel unmounts when a non-image is
  // selected). No canvas listeners are owned here, so nothing else to tear down.
  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  const scheduleApply = () => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      if (obj && obj.applyFilters) {
        obj.applyFilters();
        editor.requestRender();
      }
    });
  };

  // Set filter DATA synchronously (so history/save capture it), defer the GPU
  // texture recompute to the next frame.
  const commitFilters = (next) => {
    adjRef.current = next;
    setAdj(next);
    if (obj) {
      obj.filters = buildFilters(fabric, next);
      scheduleApply();
    }
  };

  const onSlider = (key) => (e) => {
    const v = parseInt(e.target.value, 10);
    commitFilters({ ...adjRef.current, [key]: v });
    editor.pushHistoryDebounced('Ajustare imagine');
  };

  const onToggle = (key) => () => {
    commitFilters({ ...adjRef.current, [key]: !adjRef.current[key] });
    editor.pushHistory('Filtru');
  };

  const resetAll = () => {
    commitFilters({ ...DEFAULT_ADJ });
    const b = { color: '#000000', width: 0 };
    borderRef.current = b;
    setBorder(b);
    setRadius(0);
    if (obj) {
      obj.set({ stroke: null, strokeWidth: 0 });
      obj.clipPath = null;
      obj.dirty = true;
      obj.setCoords();
    }
    editor.requestRender();
    editor.pushHistory('Reset ajustari');
  };

  const flip = (axis) => () => {
    if (!obj) return;
    obj.set(axis, !obj[axis]);
    obj.setCoords();
    editor.requestRender();
    editor.pushHistory(axis === 'flipX' ? 'Oglindire orizontala' : 'Oglindire verticala');
  };

  const onBorderColor = (e) => {
    const color = e.target.value;
    const b = { ...borderRef.current, color };
    borderRef.current = b;
    setBorder(b);
    if (obj && (obj.strokeWidth || 0) > 0) {
      obj.set('stroke', color);
      editor.requestRender();
      editor.pushHistoryDebounced('Contur imagine');
    }
  };

  const onBorderWidth = (e) => {
    const width = parseInt(e.target.value, 10) || 0;
    const b = { ...borderRef.current, width };
    borderRef.current = b;
    setBorder(b);
    if (!obj) return;
    obj.set({ stroke: width > 0 ? b.color : null, strokeWidth: width, strokeUniform: true });
    obj.setCoords();
    editor.requestRender();
    editor.pushHistoryDebounced('Contur imagine');
  };

  const onRadius = (e) => {
    const pct = parseInt(e.target.value, 10) || 0;
    setRadius(pct);
    applyRadius(fabric, obj, pct);
    editor.requestRender();
    editor.pushHistoryDebounced('Colturi rotunjite');
  };

  const openReplace = () => replaceInputRef.current && replaceInputRef.current.click();

  const onReplaceFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file || !obj) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      // editor.replaceImage keeps transform/z/selection and pushes history itself.
      editor.replaceImage(obj, ev.target.result, { label: 'Inlocuire imagine' });
    };
    reader.readAsDataURL(file);
  };

  const chip = (key, label) => (
    <button
      className={`de-btn ${adj[key] ? 'active' : ''}`}
      onClick={onToggle(key)}
      title={label}
    >{label}</button>
  );

  return (
    <div>
      {/* --- Continuous adjustments over fabric built-in filters --- */}
      <div className="ia-block">
        <div className="de-panel-title">Ajustari</div>
        <Slider label="Lumina" min={-100} max={100} value={adj.brightness} onChange={onSlider('brightness')} />
        <Slider label="Contrast" min={-100} max={100} value={adj.contrast} onChange={onSlider('contrast')} />
        <Slider label="Saturatie" min={-100} max={100} value={adj.saturation} onChange={onSlider('saturation')} />
        <Slider label="Vibranta" min={-100} max={100} value={adj.vibrance} onChange={onSlider('vibrance')} />
        <Slider label="Nuanta" min={-180} max={180} value={adj.hue} suffix={'°'} onChange={onSlider('hue')} />
        <Slider label="Blur" min={0} max={50} value={adj.blur} onChange={onSlider('blur')} />
        <Slider label="Zgomot" min={0} max={200} value={adj.noise} onChange={onSlider('noise')} />
        <Slider label="Pixeli" min={1} max={30} value={adj.pixelate} onChange={onSlider('pixelate')} />
        <button className="de-btn ia-full" onClick={resetAll} title="Reseteaza toate ajustarile imaginii">
          Reseteaza ajustarile
        </button>
      </div>

      {/* --- One-click filters --- */}
      <div className="ia-block">
        <div className="de-panel-title">Filtre</div>
        <div className="ia-grid">
          {chip('grayscale', 'Gri')}
          {chip('blackwhite', 'Alb-negru')}
          {chip('sepia', 'Sepia')}
          {chip('invert', 'Negativ')}
          {chip('sharpen', 'Claritate')}
        </div>
      </div>

      {/* --- Transform / replace --- */}
      <div className="ia-block">
        <div className="de-panel-title">Transformare</div>
        <div className="ia-flip">
          <button className="de-btn" onClick={flip('flipX')} title="Oglindeste orizontal">
            &#8646; Orizontal
          </button>
          <button className="de-btn" onClick={flip('flipY')} title="Oglindeste vertical">
            &#8645; Vertical
          </button>
        </div>
        <button className="de-btn ia-full" onClick={openReplace} title="Inlocuieste cu alta imagine (pastreaza pozitia)">
          Inlocuieste imaginea
        </button>
        <input
          ref={replaceInputRef} type="file" accept="image/*"
          style={{ display: 'none' }} onChange={onReplaceFile}
        />
      </div>

      {/* --- Border + corner radius --- */}
      <div className="ia-block">
        <div className="de-panel-title">Contur si colturi</div>
        <div className="de-row">
          <label className="de-label">Culoare</label>
          <input type="color" className="de-color" value={border.color} onChange={onBorderColor} />
        </div>
        <div className="de-row">
          <label className="de-label">Grosime</label>
          <input type="range" min="0" max="40" step="1" value={border.width} onChange={onBorderWidth} className="de-range" />
          <span className="de-val">{border.width}</span>
        </div>
        <div className="de-row">
          <label className="de-label">Colturi</label>
          <input type="range" min="0" max="100" step="1" value={radius} onChange={onRadius} className="de-range" />
          <span className="de-val">{radius}</span>
        </div>
      </div>
    </div>
  );
}

// Single registration seam into the foundation. Called from features.js's
// registerFeatures(editor). The panel is contextual: only a single image shows it.
export function registerImageAdjustments(editor) {
  bumpTextureSize(editor.fabric);
  editor.addPanelSection({
    id: 'image-adjustments',
    when: (sel) => !!sel && sel.type === 'image',
    order: 30,
    render: (ed) => <ImageAdjustmentsPanel editor={ed} obj={ed.getActive()} />,
  });
}

export default registerImageAdjustments;
